import { Vector3, type PerspectiveCamera } from 'three';
import { damp } from '../utils/rng';
import { groundAt } from '../world/heightfield';
import { FOV_DRIVE, FOV_SIGHT, type Renderer } from '../core/Renderer';

/**
 * Camera de troisieme personne, plus une vue de lunette.
 *
 * Elle suit le gisement de la *tourelle* et non le cap de la caisse. C'est le
 * point qui decide du confort de jeu : attachee a la caisse, elle ferait
 * disparaitre la cible du cadre des que la tourelle pivote, alors que c'est
 * precisement la que le joueur regarde.
 *
 * Le lissage est exponentiel et fonction du delta : un `lerp(0.1)` par frame
 * donnerait une camera deux fois plus molle a 144 Hz qu'a 60.
 * cf. doc/06-boucle-de-jeu-et-perf.md
 */

/** Vue de conduite : distance, hauteur, et point visé au-dessus du char. */
const DRIVE_DISTANCE = 15;
const DRIVE_HEIGHT = 6.4;
const DRIVE_LOOK_HEIGHT = 2.9;

/**
 * Vue de lunette : au-dessus du toit de tourelle, legerement en avant.
 *
 * Le premier reglage plaçait la camera 2,2 m *en arriere* du centre de
 * tourelle et 0,95 m au-dessus : elle se retrouvait a l'interieur de la soute,
 * et la moitie basse de l'ecran etait remplie par le toit du char. Il faut
 * passer au-dessus de la coupole (2,92 m au-dessus du sol) et avancer un peu,
 * pour que le tube reste visible en bas du cadre sans masquer le centre.
 */
const SIGHT_FORWARD = 1.1;
const SIGHT_UP = 1.45;

/**
 * Decalage lateral de la lunette, en metres (positif = vers la gauche de
 * l'ecran).
 *
 * Il est indispensable, et c'est la raison pour laquelle le viseur d'un vrai
 * char est a cote du canon et non dessus : a 25 degres de hausse, un point de
 * vue centre sur l'axe du tube a le tube en plein milieu de l'image, pile sur
 * ce qu'on essaie de regarder. Decale d'un metre, le canon sort du centre et
 * encadre la vue par la droite.
 */
const SIGHT_SIDE = 1.05;

/** Marge au-dessus du sol : la camera ne doit jamais passer sous le terrain. */
const GROUND_CLEARANCE = 1.6;

export class TurretCamera {
  private readonly desired = new Vector3();
  private readonly target = new Vector3();
  private readonly anchor = new Vector3();
  private readonly aim = new Vector3();
  private readonly look = new Vector3();
  private readonly side = new Vector3();
  private initialised = false;
  private distanceBias = 0;

  constructor(
    private readonly camera: PerspectiveCamera,
    private readonly renderer: Renderer,
  ) {}

  /**
   * @param anchor  centre de la tourelle, en coordonnees monde.
   * @param aim     direction du tube, en coordonnees monde.
   * @param sight   vrai quand le joueur tient la lunette.
   */
  /**
   * @param anchor centre de la tourelle, en coordonnees monde.
   * @param aim    direction du tube, en coordonnees monde.
   * @param impact point d'impact predit ; c'est lui que la lunette vise.
   */
  update(
    dt: number,
    anchor: Vector3,
    aim: Vector3,
    impact: Vector3 | null,
    turretYaw: number,
    speedRatio: number,
    sight: boolean,
  ): void {
    this.anchor.copy(anchor);
    this.aim.copy(aim).normalize();
    this.renderer.setFov(sight ? FOV_SIGHT : FOV_DRIVE);

    if (sight) {
      this.updateSight(dt, impact);
    } else {
      this.updateDrive(dt, turretYaw, speedRatio);
    }
  }

  private updateDrive(dt: number, turretYaw: number, speedRatio: number): void {
    // On recule un peu plus vite qu'a l'arret : le champ s'ouvre et la vitesse
    // se sent.
    const dist = Math.max(3, DRIVE_DISTANCE + this.distanceBias + speedRatio * 3.5);
    this.desired.set(
      this.anchor.x - Math.sin(turretYaw) * dist,
      this.anchor.y + DRIVE_HEIGHT + speedRatio * 0.8,
      this.anchor.z - Math.cos(turretYaw) * dist,
    );
    const floor = groundAt(this.desired.x, this.desired.z) + GROUND_CLEARANCE;
    if (this.desired.y < floor) this.desired.y = floor;

    if (!this.initialised) {
      this.camera.position.copy(this.desired);
      this.initialised = true;
    } else {
      this.camera.position.x = damp(this.camera.position.x, this.desired.x, 5.5, dt);
      this.camera.position.y = damp(this.camera.position.y, this.desired.y, 4, dt);
      this.camera.position.z = damp(this.camera.position.z, this.desired.z, 5.5, dt);
    }

    // On vise un point *devant* le char, dans l'axe du canon : sinon, canon
    // pointe en l'air, l'ecran est rempli de ciel.
    this.target.set(
      damp(this.target.x, this.anchor.x + this.aim.x * 12, 8, dt),
      damp(this.target.y, this.anchor.y + DRIVE_LOOK_HEIGHT + this.aim.y * 12, 8, dt),
      damp(this.target.z, this.anchor.z + this.aim.z * 12, 8, dt),
    );
    this.camera.lookAt(this.target);
  }

  private updateSight(dt: number, impact: Vector3 | null): void {
    // `aim x up` donne la droite de l'ecran : la camera regarde vers +z quand
    // le gisement est nul, et son axe local +x pointe alors vers -x monde.
    this.side.crossVectors(this.aim, UP).normalize();
    this.desired
      .copy(this.anchor)
      .addScaledVector(this.aim, SIGHT_FORWARD)
      .addScaledVector(UP, SIGHT_UP)
      .addScaledVector(this.side, -SIGHT_SIDE);
    const floor = groundAt(this.desired.x, this.desired.z) + 2.4;
    if (this.desired.y < floor) this.desired.y = floor;

    // A la lunette le lissage est beaucoup plus serre : un pointage fin ne
    // supporte pas une camera qui traine derriere le canon.
    const lambda = this.initialised ? 22 : 1e6;
    this.initialised = true;
    this.camera.position.x = damp(this.camera.position.x, this.desired.x, lambda, dt);
    this.camera.position.y = damp(this.camera.position.y, this.desired.y, lambda, dt);
    this.camera.position.z = damp(this.camera.position.z, this.desired.z, lambda, dt);

    // La lunette reste sur le point d'impact, pas dans l'axe du tube. Le
    // canon se releve au-dessus d'elle : c'est exactement la geometrie d'un
    // viseur de char, et c'est ce qui permet de garder l'objectif a l'ecran
    // en tirant a 250 m, ou la hausse atteint 30 degres.
    if (impact) {
      this.look.copy(impact);
    } else {
      this.look.copy(this.camera.position).addScaledVector(this.aim, 400);
    }
    // Le point visé bouge vite quand on pointe : un lissage leger evite le
    // tremblement sans ajouter de retard perceptible.
    this.target.set(
      damp(this.target.x, this.look.x, 26, dt),
      damp(this.target.y, this.look.y, 26, dt),
      damp(this.target.z, this.look.z, 26, dt),
    );
    this.camera.lookAt(this.target);
  }

  /** Rapproche ou eloigne la camera de conduite, en unites monde. */
  setDistanceBias(bias: number): void {
    this.distanceBias = bias;
  }

  snapTo(anchor: Vector3, aim: Vector3, turretYaw: number): void {
    this.initialised = false;
    this.anchor.copy(anchor);
    this.aim.copy(aim).normalize();
    this.target.copy(anchor);
    this.updateDrive(0.016, turretYaw, 0);
  }
}

const UP = new Vector3(0, 1, 0);
